<template>
  <Dialog
    v-model="useDialogStore().creditSetEdit.isOpen"
    size="small-hug"
    @open="onOpen"
    @close="onClose"
  >
    <template #header="{ close }">
      <DialogHeader
        :title="useT('addon.credits.edit.title')"
        :show-close="true"
        @close="close"
      />
    </template>

    <Form
      ref="formRef"
      :model="form"
      :rules="rules"
      @submit="onSubmit"
    >
      <!-- What the set was granted with cannot change, so it is read out in a
           callout rather than in fields that look editable. -->
      <Callout
        icon="coins-stacked-01"
        :title="useT(typeKey)"
        size="small"
        class="mb-5"
        data-cy="credit-set-facts"
      >
        <ul class="list-disc pl-4">
          <li>{{ useT('addon.credits.edit.grantedOn', { value: creditSet?.allocation || 0, date: formatDate(creditSet?.receivedAt) }) }}</li>
          <li>
            {{ creditSet?.expiringAt
              ? useT('addon.credits.expiresOn', { date: formatDate(creditSet.expiringAt) })
              : useT('addon.credits.neverExpires') }}
          </li>
        </ul>
      </Callout>

      <InputBlock
        :label="useT('addon.credits.edit.left')"
        prop="left"
        direction="horizontal"
      >
        <NumberInput
          :model-value="form.left"
          :min="0"
          class="w-full"
          data-cy="credit-set-left"
          @update:model-value="onLeft"
        />
      </InputBlock>

      <div class="text-xs text-gray-500">
        {{ useT('addon.credits.edit.hint') }}
      </div>

      <Button
        type="danger-text"
        icon-left="trash-01"
        class="mt-5"
        :loading="isRemoving"
        @click="onRemove"
      >
        {{ useT('addon.credits.remove') }}
      </Button>
    </Form>

    <template #footer>
      <ButtonGroup
        type="primary"
        :text-submit="useT('bcd15')"
        :text-cancel="useT('bcd205')"
        align="left"
        :loading="isSubmitting"
        @submit="formRef.onSubmit()"
        @cancel="onCancel"
      />
    </template>
  </Dialog>
</template>

<script setup>
// Refs
const form = ref({ left: 0 });
const rules = {};
const formRef = ref(null);
const isSubmitting = ref(false);
const isRemoving = ref(false);

// Computed
const creditSet = computed(() => useDialogStore().creditSetEdit.creditSet);
const typeKey = computed(() => useDialogStore().creditSetEdit.typeKey || 'addon.credits.type.other');

// Methods
// `receivedAt` names the set to the API; see `creditSetsMatching`.
const receivedAtOf = (set) => new Date(set.receivedAt).toISOString();

const formatDate = (value) => (value
  ? getFormattedDate(value, { lang: useAuthStore().user?.locale, format: 'll' })
  : '');

const onOpen = () => {
  form.value = { left: creditSet.value?.left || 0 };
};

const onClose = () => {
  form.value = { left: 0 };
};

const onLeft = (value) => {
  form.value.left = Number(value) || 0;
};

const closeDialog = () => {
  useDialogStore().close('creditSetEdit');
};

const onCancel = () => {
  closeDialog();
};

const onSubmit = async() => {
  const expectedLeft = creditSet.value.left || 0;

  // Nothing changed, so nothing is written or logged.
  if (form.value.left === expectedLeft) {
    closeDialog();
    return;
  }

  isSubmitting.value = true;
  // `expectedLeft` is the balance this dialog showed: the server writes only
  // while the set still holds it, so credits spent meanwhile are not undone.
  const response = await useAddonsStore().adjustCreditSet({
    workspaceDomain: useDialogStore().creditSetEdit.workspaceDomain,
    receivedAt: receivedAtOf(creditSet.value),
    left: form.value.left,
    expectedLeft,
  });
  isSubmitting.value = false;

  if (response) {
    useToast(useT('addon.credits.saved'));
    closeDialog();
  }
};

const onRemove = () => {
  useModalStore().open({
    type: 'danger',
    title: useT('addon.credits.removeConfirm'),
    description: useT('addon.credits.removeConfirmDescription', { left: creditSet.value.left || 0 }),
    submitButtonText: useT('addon.credits.removeConfirmSubmit'),
    cancelButtonText: useT('bcd205'),
    onSubmit: async() => {
      isRemoving.value = true;
      const response = await useAddonsStore().removeCreditSet({
        workspaceDomain: useDialogStore().creditSetEdit.workspaceDomain,
        receivedAt: receivedAtOf(creditSet.value),
        expectedLeft: creditSet.value.left || 0,
      });
      isRemoving.value = false;

      if (response) {
        useToast(useT('addon.credits.removed'));
        closeDialog();
      }
    },
  });
};
</script>
